import { createClient } from '@supabase/supabase-js';

// These will need to be replaced with actual values from your Supabase project
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'X-Client-Info': 'gaming-chatbot'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  storage: {
    storageKey: 'xkid-gaming-storage',
    retryAttempts: 3,
    retryInterval: 1000
  }
});

// Helper functions for chat history
export const saveChatHistory = async (userEmail, message, isAi = false) => {
  try {
    const { data: userData } = await supabase
      .from('user_profiles')
      .select('user_id')
      .eq('email', userEmail)
      .single();

    if (!userData) throw new Error('User profile not found');

    const { data, error } = await supabase
      .from('chat_history')
      .insert([
        {
          user_id: userData.user_id,
          message: message,
          is_ai: isAi,
          timestamp: new Date().toISOString(),
        }
      ]);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving chat history:', error);
    return null;
  }
};

export const getChatHistory = async (userEmail) => {
  try {
    let { data: userData, error: userError } = await supabase
      .from('user_profiles')
      .select('user_id')
      .eq('email', userEmail)
      .single();

    if (userError || !userData) {
      // Get user ID from auth
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create profile
      const newProfile = await createUserProfile(user.id, userEmail);
      if (!newProfile) throw new Error('Failed to create user profile');
      userData = { user_id: newProfile.user_id };
    }

    const { data, error } = await supabase
      .from('chat_history')
      .select('*')
      .eq('user_id', userData.user_id)
      .order('timestamp', { ascending: true });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching chat history:', error);
    return [];
  }
};

// User profile management
export const createUserProfile = async (userId, email) => {
  try {
    // First check if profile exists
    const { data: existingProfile, error: checkError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    if (existingProfile) {
      return existingProfile;
    }

    // Create new profile if it doesn't exist
    const { data, error } = await supabase
      .from('user_profiles')
      .insert([
        {
          user_id: userId,
          email: email,
          created_at: new Date().toISOString(),
          matchmaking_preferences: {
            game_types: [],
            skill_level: 'beginner',
            preferred_times: [],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }
      ])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating user profile:', error);
    return null;
  }
};

// Matchmaking functions
export const updateMatchmakingPreferences = async (userId, preferences) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ matchmaking_preferences: preferences })
      .eq('user_id', userId);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating matchmaking preferences:', error);
    return null;
  }
};

// Game challenges
export const saveChallenge = async (userId, challenge) => {
  try {
    const { data, error } = await supabase
      .from('challenges')
      .insert([{
        user_id: userId,
        challenge_text: challenge.text,
        game: challenge.game,
        difficulty: challenge.difficulty,
        created_at: new Date().toISOString(),
        completed: false
      }]);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving challenge:', error);
    return null;
  }
};

// Speedrun records
export const saveSpeedrunRecord = async (userId, record) => {
  try {
    const { data, error } = await supabase
      .from('speedruns')
      .insert([{
        user_id: userId,
        game: record.game,
        time: record.time,
        video_proof: record.videoUrl,
        created_at: new Date().toISOString(),
        verified: false
      }]);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving speedrun record:', error);
    return null;
  }
};

// Meme storage
export const saveMeme = async (userId, meme) => {
  try {
    const { data, error } = await supabase
      .storage
      .from('memes')
      .upload(`${userId}/${new Date().getTime()}.png`, meme.file);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving meme:', error);
    return null;
  }
}; 